import React from 'react';
import { clsx } from 'clsx';

import { TZDate } from '@date-fns/tz';

import { format } from '../../utils/date';
import { gettext } from '../../utils/text';
import { Show, ShowDateTime, ShowPriceZone, SpecialPrice } from './types';

const CURRENCY = '\u00A0€';

const displayPrice = (price: number): string => {
    if (price % 1 === 0) {
        // has no numbers after floating point
        return price.toFixed(0);
    }
    return price.toFixed(2);
};

const numberSorter = (a: number, b: number): number => {
    if (a > b) {
        return 1;
    }
    if (b > a) {
        return -1;
    }
    return 0;
};

const formatSpokenDate = (showDateTime: ShowDateTime): string => {
    const { date } = showDateTime;
    return new Intl.DateTimeFormat(DJ_CONST.languageCode, {
        timeZone: 'Europe/Tallinn',
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(date);
};

const ShowDate = ({
    showDateTime,
    dateElementId,
}: {
    showDateTime: ShowDateTime;
    dateElementId: string;
}) => {
    const { date } = showDateTime;
    const tallinnDate = new TZDate(date, 'Europe/Tallinn');
    const spokenDate = formatSpokenDate(showDateTime);
    const dateTimeAttr = format(tallinnDate, "yyyy-MM-dd'T'HH:mm");
    return (
        <>
            <div className="__date" aria-hidden>
                <span className="__weekday">
                    {format(tallinnDate, 'EEEEEE')}
                </span>
                <span className="__day">{format(tallinnDate, 'dd')}</span>
                <span className="__x">
                    {format(tallinnDate, 'MMM')}{' '}
                    <span className="__year">{tallinnDate.getFullYear()}</span>
                    <br />
                    {format(tallinnDate, 'HH:mm')}
                </span>
            </div>
            <time
                className="visually-hidden"
                id={dateElementId}
                dateTime={dateTimeAttr}
                aria-hidden
            >
                {spokenDate}.
            </time>
        </>
    );
};

const ShowPrice = ({
    price,
    specialPrices,
    priceElementId,
}: {
    price: ShowPriceZone | null;
    specialPrices: SpecialPrice[];
    priceElementId: string;
}) => {
    const prices: number[] = [];
    let priceText = '';
    let ariaLabel = '';
    if (specialPrices.length) {
        prices.push(...specialPrices.map(sp => parseFloat(String(sp.price))));
        prices.sort(numberSorter);
        priceText = `${prices
            .map(p => displayPrice(p))
            .join(' / ')}${CURRENCY}`;
        ariaLabel = `${gettext('Price')}: ${priceText}`;
    } else if (price) {
        const { green_price, yellow_price, red_price, blue_price } = price;
        prices.push(parseFloat(String(green_price)));
        prices.push(parseFloat(String(yellow_price)));
        prices.push(parseFloat(String(red_price)));
        prices.push(parseFloat(String(blue_price)));
        prices.sort(numberSorter);
        priceText = `${displayPrice(prices[0])}${CURRENCY} - ${displayPrice(
            prices[prices.length - 1],
        )}${CURRENCY}`;
        ariaLabel = `${gettext('Price')} ${priceText}.`;
    }
    return (
        <span className="__price" id={priceElementId} aria-label={ariaLabel}>
            {priceText}
        </span>
    );
};

const SingleShow = ({ show }: { show: Show }) => {
    let remarkBlock: React.ReactNode = null;
    let showStatusStr: boolean | null = null;
    let showStatus: string | null = null;

    if (show.cancelled) {
        showStatusStr = show.cancelled;
        showStatus = gettext('Cancelled');
    } else if (show.sold_out) {
        showStatusStr = show.sold_out;
        showStatus = gettext('Sold out');
    }

    // Accessible IDs for labeling/description
    const titleId = `show-${show.id}-title`;
    const dateId = `show-${show.id}-datetime`;
    const priceId = `show-${show.id}-price`;
    const statusId = `show-${show.id}-status`;
    const categoryId = `show-${show.id}-category`;
    const remarksId = `show-${show.id}-remarks`;

    if (show.staging.guest_staging) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__guest-staging', DJ_CONST.languageCode)}
                aria-hidden
            >
                {gettext('Guest show')}
            </div>
        );
    } else if (show.premiere) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__premiere', DJ_CONST.languageCode)}
                aria-hidden
            >
                {gettext('Premiere')}
            </div>
        );
    } else if (show.excursion) {
        remarkBlock = (
            <div
                id={remarksId}
                className={clsx('__remark', DJ_CONST.languageCode)}
                aria-hidden
            >
                + {gettext('Excursion')}
            </div>
        );
    }

    const describedByIds = [categoryId, priceId, remarkBlock ? remarksId : null]
        .filter(Boolean)
        .join(' ');

    const categories = show.staging.staging_note
        ? [show.staging.staging_note]
        : show.staging.types_of_staging;

    const spokenDate = formatSpokenDate(show.time);

    const statusParts: string[] = [];
    if (show.cancelled) {
        statusParts.push(gettext('Cancelled'));
    } else if (show.sold_out) {
        statusParts.push(gettext('Sold out'));
    }
    if (show.last_tickets) {
        statusParts.push(gettext('Last tickets'));
    }
    if (show.location) {
        statusParts.push(show.location.name);
    }

    const statusText = statusParts.length
        ? ` ${gettext('Status')}: ${statusParts.join(', ')}.`
        : '';

    return (
        <li
            className={clsx('__show position-relative', {
                '__sold-out': showStatusStr,
            })}
        >
            <span className="visually-hidden" id={categoryId}>
                {gettext('Categories')}: {categories.filter(Boolean).join(', ')}
                ,
            </span>
            <a
                href={show.url}
                aria-label={`${show.staging.name}. ${spokenDate}.${statusText}`}
                aria-describedby={describedByIds}
            >
                <div
                    className="__preview"
                    style={{ backgroundImage: `url(${show.staging.image})` }}
                    aria-hidden
                />
                <div className="__details">
                    <ShowDate showDateTime={show.time} dateElementId={dateId} />
                    <h3 className="__title" id={titleId}>
                        {show.staging.name}
                    </h3>
                    <div className="__meta">
                        <span className="__category" aria-hidden>
                            {show.staging.staging_note ? (
                                <span aria-hidden>
                                    {show.staging.staging_note}
                                </span>
                            ) : (
                                show.staging.types_of_staging.map(type => (
                                    <span aria-hidden key={type}>
                                        {type}
                                    </span>
                                ))
                            )}
                        </span>
                        <ShowPrice
                            price={show.price}
                            specialPrices={show.special_prices}
                            priceElementId={priceId}
                        />
                    </div>
                    <div className="__meta">
                        <span
                            className="__places"
                            id={showStatus ? statusId : undefined}
                            aria-label={
                                statusParts.length
                                    ? `${statusParts.join(', ')}.`
                                    : ''
                            }
                            aria-hidden={!statusParts.length}
                        >
                            <span aria-hidden>{showStatus}</span>
                            {show.last_tickets ? (
                                <span aria-hidden>
                                    {gettext('Last tickets')}
                                </span>
                            ) : null}
                            {show.location ? (
                                <span aria-hidden>{show.location.name}</span>
                            ) : null}
                        </span>
                    </div>
                </div>
                {remarkBlock}
            </a>
        </li>
    );
};

export default SingleShow;
